// T-236 — Menü am Bild im Mailtext (Rechtsklick): „Vorschau", „Speichern
// unter …" und, wenn die Mail einem Kontakt zugeordnet ist, „Beim Kontakt
// speichern". Aufbau wie `MarkierungsMenue` (T-36): portaliert, fixed, über
// den Fenstern; Escape, ein Klick daneben oder Scrollen schließt es.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { ArrowDownload20Regular, Eye20Regular, PersonArrowRight20Regular } from "@fluentui/react-icons";
import { REFINED } from "../risikoanalyseStep/tokens";
import { Z_LAYERS } from "../windowManager/zLayers";
import { alleDokumente, alleFenster, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";
import { istDom } from "../windowManager/browserFenster/domArt";

export type BildMenueProps = {
    /** Bezugspunkt in Fenster-Koordinaten (Mausposition). */
    position: { x: number; y: number };
    onVorschau: () => void;
    onSpeichernUnter: () => void;
    /** Fehlt, wenn kein Kontakt zugeordnet ist oder das Bild keinem Anhang entspricht. */
    onBeimKontaktSpeichern?: () => void;
    kontaktName?: string | null;
    onSchliessen: () => void;
};

export const BildMenue: React.FC<BildMenueProps> = ({
    position,
    onVorschau,
    onSpeichernUnter,
    onBeimKontaktSpeichern,
    kontaktName,
    onSchliessen,
}) => {
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        const beiTaste = (e: KeyboardEvent) => {
            if (e.key === "Escape") onSchliessen();
        };
        const beiKlick = (e: MouseEvent) => {
            if (ref.current && istDom(e.target, "Node") && ref.current.contains(e.target)) return;
            onSchliessen();
        };
        const beiScroll = () => onSchliessen();
        alleDokumente.addEventListener("keydown", beiTaste);
        alleDokumente.addEventListener("mousedown", beiKlick);
        alleFenster.addEventListener("scroll", beiScroll, true);
        return () => {
            alleDokumente.removeEventListener("keydown", beiTaste);
            alleDokumente.removeEventListener("mousedown", beiKlick);
            alleFenster.removeEventListener("scroll", beiScroll, true);
        };
    }, [onSchliessen]);

    React.useEffect(() => {
        ref.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
    }, []);

    // Im sichtbaren Bereich halten.
    const x = Math.max(8, Math.min(position.x, (typeof window !== "undefined" ? aktivesFenster().innerWidth : 1200) - 260 - 8));
    const y = Math.max(8, Math.min(position.y, (typeof window !== "undefined" ? aktivesFenster().innerHeight : 800) - 140));

    const waehle = (aktion: () => void) => () => {
        onSchliessen();
        aktion();
    };

    return createPortal(
        <div ref={ref} style={{ position: "fixed", left: x, top: y, zIndex: Z_LAYERS.ueberFenster + 5 }}>
            <div role="menu" aria-label="Bild im Mailtext" style={S.menue}>
                <button type="button" role="menuitem" style={S.eintrag} onClick={waehle(onVorschau)}>
                    <Eye20Regular /> Vorschau
                </button>
                <button type="button" role="menuitem" style={S.eintrag} onClick={waehle(onSpeichernUnter)}>
                    <ArrowDownload20Regular /> Speichern unter …
                </button>
                {onBeimKontaktSpeichern && (
                    <button type="button" role="menuitem" style={S.eintrag} onClick={waehle(onBeimKontaktSpeichern)}>
                        <PersonArrowRight20Regular /> {kontaktName ? `Bei ${kontaktName} speichern` : "Beim Kontakt speichern"}
                    </button>
                )}
            </div>
        </div>,
        document.body,
    );
};

const S: Record<string, React.CSSProperties> = {
    menue: {
        display: "flex",
        flexDirection: "column",
        minWidth: 220,
        maxWidth: 320,
        background: "#fff",
        border: `1px solid ${REFINED.line}`,
        borderRadius: 10,
        padding: 4,
        boxShadow: "0 12px 32px rgba(45,20,43,0.18)",
        fontFamily: REFINED.fontUI,
    },
    eintrag: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        border: "none",
        background: "transparent",
        color: REFINED.ink,
        textAlign: "left",
        padding: "8px 10px",
        borderRadius: 6,
        fontSize: 13,
        cursor: "pointer",
    },
};

export default BildMenue;
